/**
 * cart-module.js — Deep Cart module for AIGO theme
 *
 * Registers as window.FoxTheme.Cart and provides a single, stable interface
 * for all cart state reads, mutations, and shipping queries.
 *
 * Loaded after theme.js (which initialises FoxTheme and FoxTheme.routes).
 *
 * After every mutation the module dispatches:
 *   • FoxTheme.pubsub cartUpdate  — for Hyper components (sticky-atc-bar, etc.)
 * All AIGO UI modules (aigo-cart-drawer, aigo-quick-add) use Cart.subscribe() directly.
 */
(function () {
  'use strict';

  window.FoxTheme = window.FoxTheme || {};

  // ── Internal state ────────────────────────────────────────────────────────────

  /** @type {Object|null} Last known cart state from Shopify /cart.js */
  var _cartState = null;

  /** @type {Function[]} Active subscriber callbacks */
  var _subscribers = [];

  // Shipping internal state (absorbed from aigo-shipping.js)
  /** @type {Array} Fetched shipping rates, sorted cheapest-first */
  var _rates = [];
  /** @type {number} Index into _rates for the currently selected rate */
  var _selectedRateIdx = 0;
  /** @type {Object|null} Manually stored address (set via setAddress) */
  var _storedAddress = null;

  // Checkout state
  /** @type {number} Promo discount in cents */
  var _promoCents = 0;

  // ── Helpers ───────────────────────────────────────────────────────────────────

  /**
   * Build base POST config reusing FoxTheme.utils.fetchConfig when available,
   * falling back to a plain equivalent if the module loads before theme.js
   * (should not happen in practice since theme.js is deferred first).
   */
  function _fetchConfig(type, method) {
    type   = type   || 'json';
    method = method || 'POST';
    if (window.FoxTheme && window.FoxTheme.utils && window.FoxTheme.utils.fetchConfig) {
      return window.FoxTheme.utils.fetchConfig(type, method);
    }
    return {
      method:  method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/' + type },
    };
  }

  /** Resolve the cart base URL from FoxTheme.routes or fall back to '/cart'. */
  function _cartUrl() {
    return (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_url) || '/cart';
  }

  function _cartAddUrl() {
    return (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_add_url) || '/cart/add.js';
  }

  function _cartChangeUrl() {
    return (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_change_url) || '/cart/change.js';
  }

  function _cartUpdateUrl() {
    return (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_update_url) || '/cart/update.js';
  }

  // ── Notification ──────────────────────────────────────────────────────────────

  /**
   * Notify all Cart subscribers and fire the compatibility shim events.
   * @param {Object} state    - Current cart state
   * @param {Object} sections - Section HTML map from Shopify section rendering API (may be undefined)
   */
  function _notify(state, sections) {
    var payload = { state: state, sections: sections || {} };

    // Notify new-style Cart subscribers
    _subscribers.forEach(function (fn) {
      try { fn(payload); } catch (e) { console.error('[FoxTheme.Cart] subscriber error', e); }
    });

    // FoxTheme pubsub — Hyper components (CartItems, CartCount, etc.) listen here
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      window.FoxTheme.pubsub.publish(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        { source: 'cart-module', cart: state }
      );
    }
  }

  // ── Cart fetch (GET /cart.js) ─────────────────────────────────────────────────

  /**
   * Fetch the current cart from Shopify and update internal state.
   * @returns {Promise<Object>} cart state
   */
  function _fetchCart() {
    var config = _fetchConfig('json', 'GET');
    return fetch(_cartUrl() + '.js', config)
      .then(function (res) {
        if (!res.ok) throw new Error('[FoxTheme.Cart] GET /cart.js failed: ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        _cartState = cart;
        return cart;
      });
  }

  // ── Mutation helpers ──────────────────────────────────────────────────────────

  /**
   * Post a mutation to a Shopify cart endpoint and notify subscribers.
   * Sections in the response body (from the section rendering API) are passed
   * through to subscribers as `sections`.
   *
   * Shopify returns sections as `response.sections` when the request body
   * includes a `sections` array (section rendering API).
   *
   * @param {string}  url              - Endpoint URL
   * @param {Object}  body             - Request body (will be JSON-stringified; never mutated)
   * @param {Object}  opts             - { sections: string[] } — optional section IDs
   * @param {boolean} needsFollowUpGet - true when the endpoint response is NOT the full cart
   *                                     (e.g. /cart/add.js returns the added item, not the cart)
   * @returns {Promise<{state, sections}>}
   */
  function _mutate(url, body, opts, needsFollowUpGet) {
    opts = opts || {};

    // Copy body to avoid mutating the caller's object, then attach sections param
    var requestBody = {};
    Object.keys(body).forEach(function (k) { requestBody[k] = body[k]; });
    if (opts.sections && opts.sections.length) {
      requestBody.sections = opts.sections;
    }

    var config    = _fetchConfig('json', 'POST');
    config.body   = JSON.stringify(requestBody);

    return fetch(url, config)
      .then(function (res) {
        if (!res.ok) throw new Error('[FoxTheme.Cart] POST ' + url + ' failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        // The mutation response IS the cart for change/update.
        // Sections are nested under data.sections when requested.
        var sections = data.sections || {};

        // Store the cart state. For /cart/add.js the response is the added item,
        // not the full cart — so we need to follow up with a GET (needsFollowUpGet=true).
        // For /cart/change.js and /cart/update.js the response IS the full cart.
        var cartPromise;
        if (needsFollowUpGet) {
          // add.js returns the added line item — fetch the full cart separately
          cartPromise = _fetchCart();
        } else {
          _cartState = data;
          cartPromise = Promise.resolve(data);
        }

        return cartPromise.then(function (cart) {
          _notify(cart, sections);
          return { state: cart, sections: sections };
        });
      });
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  var Cart = {

    // ── State ───────────────────────────────────────────────────────────────────

    /**
     * Returns the last known cart state object, or null before first fetch.
     * @returns {Object|null}
     */
    getState: function () {
      return _cartState;
    },

    /**
     * Subscribe to cart changes. The callback receives { state, sections }
     * after every successful mutation.
     *
     * @param {Function} fn
     * @returns {Function} unsubscribe — call to remove this subscriber
     */
    subscribe: function (fn) {
      _subscribers.push(fn);
      return function unsubscribe() {
        _subscribers = _subscribers.filter(function (s) { return s !== fn; });
      };
    },

    // ── Mutations ────────────────────────────────────────────────────────────────

    /**
     * Add one or more items to the cart.
     *
     * NOTE: opts.sections has no effect for add() — /cart/add.js does not return section
     * HTML even when sections are included in the request body. Subscribers receive
     * { state, sections: {} } after an add. Use Cart.change() or Cart.update() for sections.
     *
     * @param {Array}  items  - [{id, quantity, ...}, ...]
     * @param {Object} opts   - { sections: string[] } — has no effect for add(); see note above
     * @returns {Promise<{state, sections}>}
     */
    add: function (items, opts) {
      return _mutate(_cartAddUrl(), { items: items }, opts, true); // true = follow-up GET needed
    },

    /**
     * Change the quantity of a line item by key.
     *
     * @param {string} key  - Line item key
     * @param {number} qty  - New quantity (0 = remove)
     * @param {Object} opts - { sections: string[] }
     * @returns {Promise<{state, sections}>}
     */
    change: function (key, qty, opts) {
      return _mutate(_cartChangeUrl(), { id: key, quantity: qty }, opts);
    },

    /**
     * Update cart-level attributes (note, discount, attributes, etc.).
     *
     * @param {Object} attrs - e.g. { note: '...', discount: 'CODE', attributes: {...} }
     * @param {Object} opts  - { sections: string[] }
     * @returns {Promise<{state, sections}>}
     */
    update: function (attrs, opts) {
      return _mutate(_cartUpdateUrl(), attrs, opts);
    },

    /**
     * Clear the cart silently. Subscribers are NOT notified — use when
     * resetting state without triggering UI updates.
     *
     * @returns {Promise<Object>} cleared cart state
     */
    clear: function () {
      var config  = _fetchConfig('json', 'POST');
      config.body = JSON.stringify({});
      return fetch(_cartUrl() + '/clear.js', config)
        .then(function (res) {
          if (!res.ok) throw new Error('[FoxTheme.Cart] POST ' + _cartUrl() + '/clear.js failed: ' + res.status);
          return res.json();
        })
        .then(function (cart) {
          _cartState = cart;
          return cart;
        });
    },

    // ── Shipping ─────────────────────────────────────────────────────────────────

    /**
     * Fetch shipping rates for the given address from Shopify.
     * Updates internal rate state (used by getSelectedRate).
     *
     * @param {Object} address - { zip, country, province, ... }
     * @returns {Promise<Array>} sorted rates array
     */
    fetchShippingRates: function (address) {
      return fetch('/cart/shipping_rates.json', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body:    JSON.stringify({ shipping_address: { zip: address.zip, country: address.country, province: address.province } }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('[FoxTheme.Cart] shipping_rates fetch failed: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (Array.isArray(data.shipping_rates)) {
            _rates = data.shipping_rates.slice().sort(function (a, b) {
              return parseFloat(a.price) - parseFloat(b.price);
            });
            _selectedRateIdx = 0;
          } else {
            _rates = [];
            _selectedRateIdx = 0;
          }
          return _rates;
        })
        .catch(function (err) {
          console.error('[FoxTheme.Cart] fetchShippingRates error', err);
          _rates = [];
          _selectedRateIdx = 0;
          throw err;
        });
    },

    /**
     * Returns the currently selected shipping rate, or null if none loaded.
     * @returns {{ title: string, price: string, currency: string }|null}
     */
    getSelectedRate: function () {
      if (!_rates.length) return null;
      var r = _rates[_selectedRateIdx];
      return { title: r.presentment_name, price: r.price, currency: r.currency };
    },

    /**
     * Set the selected rate by index (matches the sorted rates array index).
     * @param {{ index: number }|number} rate - index or object with .index
     */
    setSelectedRate: function (rate) {
      var idx = (typeof rate === 'number') ? rate : (rate && rate.index != null ? rate.index : 0);
      if (idx >= 0 && idx < _rates.length) {
        _selectedRateIdx = idx;
      }
    },

    /**
     * Read the active address. Returns the value stored via setAddress() if present;
     * otherwise reads from whichever form is visible (desktop co-* / mobile mco-*).
     * Copied exactly from aigo-shipping.js getAddress() to preserve field-ID mapping.
     * @returns {Object} address
     */
    getAddress: function () {
      if (_storedAddress) return _storedAddress;

      var isMobile = window.matchMedia('(max-width: 900px)').matches;
      var prefix   = isMobile ? 'mco' : 'co';

      function val(id) {
        var el = document.getElementById(id);
        return el ? el.value.trim() : '';
      }

      return {
        first_name: val(prefix + '-first'),
        last_name:  val(prefix + '-last'),
        address1:   val(prefix + '-addr1'),
        address2:   val(prefix + '-addr2'),
        city:       val(prefix + '-city'),
        country:    val(prefix + '-country'),
        province:   val(prefix + '-state'),
        zip:        val(prefix + '-zip'),
      };
    },

    /**
     * Store an address object (does not write to the DOM).
     * @param {Object} address
     */
    setAddress: function (address) {
      _storedAddress = address;
    },

    // ── Promo discount ────────────────────────────────────────────────────────────

    /**
     * Returns the stored promo discount in cents.
     * @returns {number}
     */
    getPromoDiscount: function () {
      return _promoCents;
    },

    /**
     * Store promo discount amount in cents.
     * @param {number} cents
     */
    setPromoDiscount: function (cents) {
      _promoCents = typeof cents === 'number' ? cents : 0;
    },

    /**
     * Fetch the current cart from Shopify, update internal state, and notify
     * all subscribers. Call this after a direct (non–Cart-module) mutation
     * such as ProductForm's FormData POST to /cart/add.js.
     * @returns {Promise<Object>} updated cart state
     */
    refresh: function () {
      return _fetchCart().then(function (cart) {
        _notify(cart, {});
        return cart;
      });
    },
  };

  // Register on FoxTheme
  window.FoxTheme.Cart = Cart;
})();
